import React, { useState } from "react";
import cn from "clsx";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

import Loader from "components/global/Loader";

interface TooltipConfig {
  clickable?: boolean;
  openEvents?: Record<string, boolean>;
  closeEvents?: Record<string, boolean>;
  position?: "top" | "right" | "bottom" | "left";
  positionStrategy?: "absolute" | "fixed";
  globalCloseEvents?: {
    escape?: boolean;
    scroll?: boolean;
    resize?: boolean;
    clickOutsideAnchor?: boolean;
  };
}

interface ConfirmTooltipProps {
  id?: string;
  title?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  config?: TooltipConfig;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
  children?: React.ReactNode;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  closeOnConfirm?: boolean;
  contentClassName?: string;
  tooltipClassName?: string;
}

const ConfirmTooltip: React.FC<ConfirmTooltipProps> = ({
  id = "react-tooltip",
  title,
  onConfirm,
  onCancel,
  confirmText = "Yes",
  cancelText = "Cancel",
  config = {},
  isOpen: controlledOpen,
  onToggle,
  children,
  isLoading = false,
  confirmDisabled = false,
  closeOnConfirm = true,
  contentClassName,
  tooltipClassName,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = controlledOpen ?? uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (isLoading && !next) {
      return;
    }

    if (next === isOpen) {
      return;
    }

    if (onToggle) {
      onToggle(next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  const {
    clickable = true,
    openEvents: configOpenEvents,
    closeEvents: configCloseEvents,
    position = "top",
    positionStrategy = "absolute",
    globalCloseEvents = {
      escape: true,
      clickOutsideAnchor: true,
    },
  } = config;

  const openEvents = isControlled
    ? (configOpenEvents ?? {})
    : (configOpenEvents ?? { click: true });

  const closeEvents = isControlled
    ? (configCloseEvents ?? {})
    : (configCloseEvents ?? { click: true, blur: true });

  const handleClose = () => {
    onCancel?.();
    setOpen(false);
  };

  return (
    <ReactTooltip
      id={id}
      isOpen={isOpen}
      clickable={clickable}
      openEvents={openEvents}
      closeEvents={closeEvents}
      globalCloseEvents={globalCloseEvents}
      place={position}
      positionStrategy={positionStrategy}
      className={tooltipClassName}
      opacity={1}
      setIsOpen={setOpen}
    >
      <div className={cn("confirm-tooltip", contentClassName)}>
        <div className="confirm-tooltip-content">
          {title && <div className="confirm-tooltip-title">{title}</div>}
          {children}
          <div className="confirm-tooltip-action">
            <button
              className="btn left"
              type="button"
              disabled={isLoading || confirmDisabled}
              onClick={() => {
                if (isLoading || confirmDisabled) {
                  return;
                }

                onConfirm();

                if (closeOnConfirm) {
                  setOpen(false);
                }
              }}
            >
              {isLoading ? <Loader size={10} /> : confirmText}
            </button>
            <button
              className="btn right"
              type="button"
              disabled={isLoading}
              onClick={() => {
                if (isLoading) {
                  return;
                }

                handleClose();
              }}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </ReactTooltip>
  );
};

export default ConfirmTooltip;
